import { useState } from 'react';
import { Download, FileText, Mail, Sparkles } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { MeetingMinutes } from '../../lib/meetingLogApi';
import { downloadCallMinutesDocx, type VoiceCallDetail } from '../../lib/voiceCallsApi';
import {
  useCallMinutes,
  useEmailCallMinutes,
  useGenerateCallMinutes,
  useGeneratingCallMinutes,
} from '../../hooks/useVoiceCalls';

/**
 * „Minuta discuției” on a voice-room call — the document the meetings page
 * writes for a meeting, written for a call in a channel of the team chat.
 *
 * Read on the page, not only in Word: the point of a minute for an internal
 * call is to see in ten seconds what was talked about, so the whole thing is
 * rendered here (summary, sections, decisions, who took what on) and the Word
 * file is for sending it on. Every generation is a paid API call, so the card
 * says what each one cost and nothing is written without someone pressing the
 * button.
 */
export interface VoiceCallMinutesProps {
  call: VoiceCallDetail;
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function formatMoney(usd: number, eur: number): string {
  return `$${usd.toFixed(3)} (≈ ${eur.toFixed(3)} €)`;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('ro-RO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `**bold**` out, plain text in — the page shows the words, Word shows the bold. */
function plain(text: string): string {
  return text.replace(/\*\*/g, '');
}

const BUTTON =
  'inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50';

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

export function VoiceCallMinutes({ call }: VoiceCallMinutesProps) {
  const { t } = useI18n();
  const live = call.endedAt === null;
  const { data: minutes = [], isLoading } = useCallMinutes(call.id);
  const generate = useGenerateCallMinutes(call.id);
  const generating = useGeneratingCallMinutes(call.id);
  const sendEmail = useEmailCallMinutes(call.id);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const latest: MeetingMinutes | undefined = minutes[0];
  const older = minutes.slice(1);
  // A live call's transcript is still growing, and a call with nothing in it
  // has nothing to read: both cases say so instead of offering the button.
  const canGenerate = !live && call.segmentCount > 0;

  const onGenerate = () => {
    setError(null);
    generate.mutate(undefined, {
      onError: (err) => setError(errorText(err, t('teamChat.minutes.failed'))),
    });
  };

  const onSendEmail = (m: MeetingMinutes) => {
    setError(null);
    sendEmail.mutate(m, {
      onError: (err) => setError(errorText(err, t('teamChat.minutes.failed'))),
    });
  };

  const onDownload = async (m: MeetingMinutes) => {
    setError(null);
    setDownloading(m.id);
    try {
      await downloadCallMinutesDocx(call.id, m);
    } catch (err) {
      setError(errorText(err, t('teamChat.minutes.failed')));
    } finally {
      setDownloading(null);
    }
  };

  const content = latest?.content;

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <FileText size={14} />
            {t('teamChat.minutes.title')}
          </h2>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating || !canGenerate}
          title={
            live
              ? t('teamChat.minutes.live')
              : !canGenerate
                ? t('teamChat.minutes.needsTranscript')
                : undefined
          }
          className={BUTTON}
        >
          <Sparkles size={15} className={generating ? 'animate-pulse' : undefined} />
          {generating
            ? t('teamChat.minutes.generating')
            : minutes.length
              ? t('teamChat.minutes.regenerate')
              : t('teamChat.minutes.generate')}
        </button>
      </div>

      <div className="rounded-lg border border-border bg-card px-4 py-3">
        <p className="text-xs text-muted-foreground">{t('teamChat.minutes.hint')}</p>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        {isLoading ? (
          <p className="mt-3 text-sm text-muted-foreground">…</p>
        ) : !latest || !content ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {generating
              ? t('teamChat.minutes.generating')
              : live
                ? t('teamChat.minutes.live')
                : canGenerate
                  ? t('teamChat.minutes.empty')
                  : t('teamChat.minutes.needsTranscript')}
          </p>
        ) : (
          <div className="mt-3 border-t border-border pt-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground">{content.title}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {content.meetingTypeLabel} · {t('teamChat.minutes.generatedAt')}{' '}
                  {formatWhen(latest.createdAt)} · {latest.model} · {t('teamChat.minutes.cost')}{' '}
                  {formatMoney(latest.costUsd, latest.costEur)} ·{' '}
                  {latest.inputTokens.toLocaleString('ro-RO')} +{' '}
                  {latest.outputTokens.toLocaleString('ro-RO')} {t('teamChat.minutes.tokens')}
                </div>
                {latest.emailedTo.length > 0 && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t('teamChat.minutes.emailedTo')} {latest.emailedTo.join(', ')}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onSendEmail(latest)}
                  disabled={sendEmail.isPending}
                  className={BUTTON}
                >
                  <Mail size={15} />
                  {sendEmail.isPending
                    ? t('teamChat.minutes.sending')
                    : t('teamChat.minutes.sendEmail')}
                </button>
                <button
                  type="button"
                  onClick={() => void onDownload(latest)}
                  disabled={downloading === latest.id}
                  className={BUTTON}
                >
                  <Download size={15} />
                  {t('teamChat.minutes.download')}
                </button>
              </div>
            </div>

            <div className="mt-3 space-y-2 text-sm text-foreground">
              {content.summary.map((p, i) => (
                <p key={i}>{plain(p)}</p>
              ))}
            </div>

            {content.topics.map((topic, i) => (
              <SubSection key={i} title={topic.heading}>
                {topic.intro && (
                  <p className="mt-1 text-sm text-foreground">{plain(topic.intro)}</p>
                )}
                <ul className="mt-1 space-y-1 text-sm text-foreground">
                  {topic.bullets.map((b, j) => (
                    <li key={j} className="flex gap-2">
                      <span className="text-muted-foreground">·</span>
                      <span>
                        {b.lead && <span className="font-semibold">{plain(b.lead)} </span>}
                        {plain(b.text)}
                      </span>
                    </li>
                  ))}
                </ul>
                {topic.outro && (
                  <p className="mt-1 text-sm text-foreground">{plain(topic.outro)}</p>
                )}
              </SubSection>
            ))}

            {content.keyFacts.length > 0 && (
              <SubSection title={t('teamChat.minutes.keyFacts')}>
                <ul className="mt-1 space-y-1 text-sm text-foreground">
                  {content.keyFacts.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-muted-foreground">·</span>
                      <span>
                        {b.lead && <span className="font-semibold">{plain(b.lead)} </span>}
                        {plain(b.text)}
                      </span>
                    </li>
                  ))}
                </ul>
              </SubSection>
            )}

            {content.decisions.length > 0 && (
              <SubSection title={t('teamChat.minutes.decisions')}>
                <ol className="mt-1 space-y-1 text-sm text-foreground">
                  {content.decisions.map((d, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-semibold text-primary">{i + 1}.</span>
                      <span>{plain(d)}</span>
                    </li>
                  ))}
                </ol>
              </SubSection>
            )}

            {content.actions.length > 0 && (
              <SubSection title={t('teamChat.minutes.actions')}>
                <ul className="mt-1 space-y-1 text-sm text-foreground">
                  {content.actions.map((a, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-semibold text-primary">{i + 1}.</span>
                      <span>
                        {plain(a.action)} <span className="text-muted-foreground">— {a.owner}</span>
                        <span className="text-muted-foreground">
                          {' · '}
                          {a.dueDate ?? t('teamChat.minutes.noDueDate')}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </SubSection>
            )}

            {content.openQuestions.length > 0 && (
              <SubSection title={t('teamChat.minutes.openQuestions')}>
                <ul className="mt-1 space-y-1 text-sm text-foreground">
                  {content.openQuestions.map((q, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-muted-foreground">·</span>
                      <span>{plain(q)}</span>
                    </li>
                  ))}
                </ul>
              </SubSection>
            )}

            {content.nextSteps.trim() && (
              <SubSection title={t('teamChat.minutes.nextSteps')}>
                <p className="mt-1 text-sm text-foreground">{plain(content.nextSteps)}</p>
              </SubSection>
            )}

            {older.length > 0 && (
              <div className="mt-4 border-t border-border pt-3">
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {t('teamChat.minutes.history')}
                </div>
                <ul className="mt-1.5 divide-y divide-border text-sm">
                  {older.map((m) => (
                    <li
                      key={m.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-1.5"
                    >
                      <span className="text-muted-foreground">
                        {formatWhen(m.createdAt)} · {m.model} · {formatMoney(m.costUsd, m.costEur)}
                      </span>
                      <button
                        type="button"
                        onClick={() => void onDownload(m)}
                        disabled={downloading === m.id}
                        className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
                      >
                        <Download size={14} />
                        {t('teamChat.minutes.download')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
